/**
 * Pre-Authentication API Utility
 *
 * Dedicated utility for API endpoints called BEFORE authentication exists.
 * These functions do NOT inject JWT tokens (prevents chicken-and-egg problem).
 *
 * Use cases:
 * - User login
 * - User registration
 * - Username lookup
 * - Password reset requests
 *
 * After authentication is established, use the managed API client (utils/api.ts)
 * for automatic JWT token handling and refresh.
 */

import { extractApiErrorMessage } from './errorUtils';

/**
 * Get API base URL from window config or environment variable
 */
function getApiUrl(): string {
  const windowWithConfig = window as { APP_CONFIG?: { API_URL?: string } };
  return (
    windowWithConfig.APP_CONFIG?.API_URL ||
    import.meta.env.VITE_API_URL ||
    'https://api-a.on.artlegacyinstitute.org'
  );
}

/**
 * Make a POST request to a pre-authentication endpoint
 * No JWT token injection - use before user is authenticated
 *
 * @param endpoint - API endpoint path (e.g., '/auth/login', '/register')
 * @param body - Request body to send as JSON
 * @returns Promise resolving to response data
 * @throws Error if network request fails
 */
export async function preAuthPost(endpoint: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const apiUrl = getApiUrl();
  const url = `${apiUrl}${endpoint}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(
      extractApiErrorMessage(data, response.status)
    );
    (error as Error & { response: { status: number; data: unknown } }).response = {
      status: response.status,
      data,
    };
    throw error;
  }

  return data;
}

/**
 * Make a GET request to a pre-authentication endpoint
 * No JWT token injection - use before user is authenticated
 *
 * @param endpoint - API endpoint path (e.g., '/api/auth/lookup-username')
 * @param params - Optional query parameters as key-value pairs
 * @returns Promise resolving to response data
 * @throws Error if network request fails
 */
export async function preAuthGet(
  endpoint: string,
  params?: Record<string, string>
): Promise<Record<string, unknown>> {
  const apiUrl = getApiUrl();

  // Build query string if params provided
  let url = `${apiUrl}${endpoint}`;
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    url = `${url}?${queryString}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(
      extractApiErrorMessage(data, response.status)
    );
    (error as Error & { response: { status: number; data: unknown } }).response = {
      status: response.status,
      data,
    };
    throw error;
  }

  return data;
}
